import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Plus, RotateCw, X } from "lucide-react";
import FilterPopover from "../components/FilterPopover";
import {
    addClientRequestMessage,
    closeClientRequest,
    createClientRequest,
    fetchClientRequestAttachmentBlob,
    listClientRequests,
    uploadClientRequestAttachment,
} from "../services/requests";
import { fetchClientAccessOverview } from "../services/profile";
import { useWebSocket } from "../context/WebSocketContext";
import { fetchMyRestrictions } from "../services/clientRestrictions";
import RestrictionNotice from "../components/RestrictionNotice";
import { formatRestrictionMessage, isModuleReadonly } from "../utils/clientRestrictionsUi";
import "../styles/chat.css";

function AuthImage({ fileUrl, alt, className }) {
    const [src, setSrc] = useState("");
    useEffect(() => {
        let revoked = false;
        let objectUrl = "";
        (async () => {
            try {
                const blob = await fetchClientRequestAttachmentBlob(fileUrl);
                if (revoked) return;
                objectUrl = URL.createObjectURL(blob);
                setSrc(objectUrl);
            } catch (_) {
                // ignore
            }
        })();
        return () => {
            revoked = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [fileUrl]);
    if (!src) return <div className={`request-photo-skeleton ${className || ""}`} />;
    return <img src={src} alt={alt || "Фото"} className={className} />;
}

function PhotoStrip({ attachments }) {
    const list = Array.isArray(attachments) ? attachments : [];
    if (!list.length) return null;
    return (
        <div className="request-photo-strip">
            {list.map((att) => (
                <a
                    key={att.id}
                    className="request-photo-thumb"
                    href="#photo"
                    onClick={async (e) => {
                        e.preventDefault();
                        try {
                            const blob = await fetchClientRequestAttachmentBlob(att.file_url);
                            const url = URL.createObjectURL(blob);
                            window.open(url, "_blank", "noopener,noreferrer");
                            setTimeout(() => URL.revokeObjectURL(url), 60_000);
                        } catch (_) {
                            // ignore
                        }
                    }}
                >
                    <AuthImage fileUrl={att.file_url} alt={att.file_name} />
                </a>
            ))}
        </div>
    );
}

const ClientRequests = () => {
    const [description, setDescription] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [statusFilter, setStatusFilter] = useState("open");
    const [scopeFilter, setScopeFilter] = useState("network:all");
    const [accessOverview, setAccessOverview] = useState({ contexts: [] });
    const [highlightedRequestId, setHighlightedRequestId] = useState(null);
    const [expandedRequestId, setExpandedRequestId] = useState(null);
    const [report, setReport] = useState({ open: 0, closed: 0 });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createNetworkId, setCreateNetworkId] = useState("");
    const [createLocationId, setCreateLocationId] = useState("");
    const [createFiles, setCreateFiles] = useState([]);
    const [draftComments, setDraftComments] = useState({});
    const [closeComments, setCloseComments] = useState({});
    const [myRestriction, setMyRestriction] = useState(null);
    const fileInputRef = useRef(null);
    const requestsReadonly = isModuleReadonly(myRestriction, "requests");
    const requestRefs = useRef(new Map());
    const clientId = parseInt(localStorage.getItem("clientId"), 10);
    const { connect, subscribe, unsubscribe } = useWebSocket();

    const parseScopeFilter = useCallback((value) => {
        if (value === "mine") return { scope: "mine", networkId: null };
        if (typeof value === "string" && value.startsWith("network:")) {
            const raw = value.slice("network:".length);
            if (!raw || raw === "all") return { scope: "network", networkId: null };
            const parsed = parseInt(raw, 10);
            return {
                scope: "network",
                networkId: Number.isFinite(parsed) && parsed > 0 ? parsed : null,
            };
        }
        return { scope: "network", networkId: null };
    }, []);

    const connectedNetworks = useMemo(() => Object.values(
        (accessOverview?.contexts || []).reduce((acc, context) => {
            if (!context?.network_id) return acc;
            const key = String(context.network_id);
            if (!acc[key]) {
                acc[key] = {
                    id: context.network_id,
                    name: context.network_name || `Мережа #${context.network_id}`,
                };
            }
            return acc;
        }, {}),
    ), [accessOverview]);

    const locationsForCreate = useMemo(() => {
        const networkId = createNetworkId
            ? parseInt(createNetworkId, 10)
            : (connectedNetworks.length === 1 ? connectedNetworks[0].id : null);
        if (!networkId) return [];
        const map = {};
        (accessOverview?.contexts || []).forEach((ctx) => {
            if (Number(ctx?.network_id) !== Number(networkId) || !ctx?.location_id) return;
            const key = String(ctx.location_id);
            if (!map[key]) {
                const street = (ctx.street || "").trim();
                const house = (ctx.house_number || "").trim();
                const addr = [street, house].filter(Boolean).join(", ");
                const name = (ctx.location_name || `Локація #${ctx.location_id}`).trim();
                map[key] = {
                    id: ctx.location_id,
                    label: addr ? `${name} — ${addr}` : name,
                };
            }
        });
        return Object.values(map).sort((a, b) => a.label.localeCompare(b.label, "uk"));
    }, [accessOverview, createNetworkId, connectedNetworks]);

    const scopeOptions = useMemo(() => {
        const options = [
            { value: "network:all", label: "Всі в моїх мережах" },
            { value: "mine", label: "Лише мої" },
        ];
        connectedNetworks.forEach((network) => {
            options.push({
                value: `network:${network.id}`,
                label: network.name || `Мережа #${network.id}`,
            });
        });
        return options;
    }, [connectedNetworks]);

    const selectedScopeMeta = useMemo(() => parseScopeFilter(scopeFilter), [parseScopeFilter, scopeFilter]);

    const load = useCallback(async () => {
        setLoading(true);
        setMessage("");
        try {
            const { scope, networkId } = parseScopeFilter(scopeFilter);
            const baseParams = {
                scope,
                network_id: scope === "network" ? networkId : null,
            };
            const requestsPromise = listClientRequests({
                ...baseParams,
                status: statusFilter || undefined,
            });
            const openPromise = statusFilter === "open"
                ? requestsPromise
                : listClientRequests({ ...baseParams, status: "open" });
            const closedPromise = statusFilter === "closed"
                ? requestsPromise
                : listClientRequests({ ...baseParams, status: "closed" });

            const [data, overview, openItems, closedItems] = await Promise.all([
                requestsPromise,
                fetchClientAccessOverview(),
                openPromise,
                closedPromise,
            ]);

            setItems(Array.isArray(data) ? data : []);
            setAccessOverview(overview || { contexts: [] });
            setReport({
                open: Array.isArray(openItems) ? openItems.length : 0,
                closed: Array.isArray(closedItems) ? closedItems.length : 0,
            });
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося завантажити заявки"));
        } finally {
            setLoading(false);
        }
    }, [parseScopeFilter, scopeFilter, statusFilter]);

    useEffect(() => {
        if (connectedNetworks.length === 0) return;
        setScopeFilter((prev) => {
            if (prev === "mine" || prev === "network:all") return prev;
            const parsed = parseScopeFilter(prev);
            if (parsed.networkId && connectedNetworks.some((n) => Number(n.id) === Number(parsed.networkId))) {
                return prev;
            }
            if (connectedNetworks.length === 1) return `network:${connectedNetworks[0].id}`;
            return "network:all";
        });
    }, [connectedNetworks, parseScopeFilter]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (!Number.isFinite(clientId) || clientId <= 0) return;
        const roomId = `client:${clientId}`;
        const handleRequestChanged = (payload) => {
            const requestId = parseInt(payload?.id ?? payload?.request_id, 10);
            if (Number.isFinite(requestId) && requestId > 0) {
                try { localStorage.setItem("pendingRequestOpenId_client", String(requestId)); } catch (_) {}
            }
            load();
        };
        connect("requests", roomId);
        subscribe("requests", roomId, "request_changed", handleRequestChanged);
        return () => unsubscribe("requests", roomId, "request_changed", handleRequestChanged);
    }, [connect, subscribe, unsubscribe, clientId, load]);

    useEffect(() => {
        if (selectedScopeMeta.scope !== "network") return undefined;
        if (connectedNetworks.length === 0) return undefined;
        const cleanups = [];
        connectedNetworks.forEach((network) => {
            if (!network?.id) return;
            const roomId = `network:${network.id}`;
            const handleRequestChanged = (payload) => {
                const requestId = parseInt(payload?.id ?? payload?.request_id, 10);
                if (Number.isFinite(requestId) && requestId > 0) {
                    try { localStorage.setItem("pendingRequestOpenId_client", String(requestId)); } catch (_) {}
                }
                load();
            };
            connect("requests", roomId);
            subscribe("requests", roomId, "request_changed", handleRequestChanged);
            cleanups.push(() => unsubscribe("requests", roomId, "request_changed", handleRequestChanged));
        });
        return () => cleanups.forEach((fn) => fn());
    }, [connect, subscribe, unsubscribe, connectedNetworks, selectedScopeMeta.scope, load]);

    useEffect(() => {
        let raw = null;
        try { raw = localStorage.getItem("pendingRequestOpenId_client"); } catch (_) { raw = null; }
        if (!raw) return;
        const id = parseInt(raw, 10);
        if (!Number.isFinite(id) || id <= 0) {
            try { localStorage.removeItem("pendingRequestOpenId_client"); } catch (_) {}
            return;
        }
        const item = (items || []).find((request) => Number(request?.id) === id);
        if (!item) {
            if (statusFilter) setStatusFilter("");
            return;
        }
        try { localStorage.removeItem("pendingRequestOpenId_client"); } catch (_) {}
        setHighlightedRequestId(id);
        setExpandedRequestId(id);
        const el = requestRefs.current.get(id);
        if (el) {
            window.requestAnimationFrame(() => el.scrollIntoView({ behavior: "smooth", block: "center" }));
        }
        const timer = window.setTimeout(() => {
            setHighlightedRequestId((prev) => (prev === id ? null : prev));
        }, 60000);
        return () => window.clearTimeout(timer);
    }, [items, statusFilter]);

    useEffect(() => {
        setCreateLocationId("");
    }, [createNetworkId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchMyRestrictions();
                if (!cancelled) setMyRestriction(data);
            } catch (_) {
                if (!cancelled) setMyRestriction(null);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    const getPreview = (text) => {
        const raw = String(text || "").replace(/\s+/g, " ").trim();
        if (!raw) return "Без опису";
        return raw.length > 120 ? `${raw.slice(0, 120)}...` : raw;
    };

    const openCreateModal = () => {
        setMessage("");
        if (requestsReadonly) {
            setMessage(formatRestrictionMessage(myRestriction, "requests"));
            return;
        }
        setDescription("");
        setCreateFiles([]);
        setCreateLocationId("");
        if (connectedNetworks.length === 1) {
            setCreateNetworkId(String(connectedNetworks[0].id));
        } else {
            setCreateNetworkId("");
        }
        setShowCreateModal(true);
    };

    const onPickFiles = (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setCreateFiles((prev) => {
            const next = [...prev, ...files].slice(0, 5);
            return next;
        });
        e.target.value = "";
    };

    const submit = async () => {
        if (requestsReadonly) {
            setMessage(formatRestrictionMessage(myRestriction, "requests"));
            return;
        }
        const text = (description || "").trim();
        if (text.length < 3) {
            setMessage("⚠️ Опишіть проблему (мінімум 3 символи)");
            return;
        }

        setLoading(true);
        setMessage("");
        try {
            let networkId = createNetworkId ? parseInt(createNetworkId, 10) : null;
            if (!networkId && connectedNetworks.length === 1) {
                networkId = connectedNetworks[0].id;
            }
            if (!networkId && connectedNetworks.length > 1) {
                setMessage("⚠️ Оберіть мережу для заявки");
                setLoading(false);
                return;
            }

            const locationId = createLocationId ? parseInt(createLocationId, 10) : null;
            const created = await createClientRequest({
                description: text,
                network_id: networkId,
                location_id: Number.isFinite(locationId) ? locationId : null,
            });

            if (created?.id && createFiles.length) {
                for (const file of createFiles) {
                    await uploadClientRequestAttachment(created.id, file);
                }
            }

            setDescription("");
            setCreateNetworkId("");
            setCreateLocationId("");
            setCreateFiles([]);
            setShowCreateModal(false);
            setStatusFilter("open");
            await load();
            if (created?.id) {
                setExpandedRequestId(created.id);
                setHighlightedRequestId(created.id);
            }
            setMessage("✅ Заявку створено");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося створити заявку"));
        } finally {
            setLoading(false);
        }
    };

    const sendComment = async (requestId) => {
        if (requestsReadonly) {
            setMessage(formatRestrictionMessage(myRestriction, "requests"));
            return;
        }
        const body = String(draftComments[requestId] || "").trim();
        if (!body) {
            setMessage("⚠️ Напишіть коментар");
            return;
        }
        setLoading(true);
        setMessage("");
        try {
            await addClientRequestMessage(requestId, body);
            setDraftComments((prev) => ({ ...prev, [requestId]: "" }));
            await load();
            setMessage("✅ Коментар додано");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося додати коментар"));
        } finally {
            setLoading(false);
        }
    };

    const doClose = async (requestId) => {
        const comment = String(closeComments[requestId] || "").trim();
        setLoading(true);
        setMessage("");
        try {
            await closeClientRequest(requestId, comment || null);
            setCloseComments((prev) => ({ ...prev, [requestId]: "" }));
            await load();
            setMessage("✅ Заявку закрито");
        } catch (e) {
            setMessage("❌ " + (e?.message || "Не вдалося закрити заявку"));
        } finally {
            setLoading(false);
        }
    };

    const filterActive = Boolean(statusFilter !== "open" || scopeFilter !== "network:all");

    return (
        <div className="operator-page-width-chat">
            <div className="config-header">
                <div className="header-buttons">
                    <button
                        className="add-btn toolbar-icon-btn"
                        type="button"
                        onClick={openCreateModal}
                        title="Нова заявка"
                        aria-label="Нова заявка"
                    >
                        <Plus size={16} />
                    </button>
                    <button
                        className="add-btn refresh toolbar-icon-btn"
                        type="button"
                        onClick={load}
                        disabled={loading}
                        title="Оновити"
                        aria-label="Оновити"
                    >
                        <RotateCw size={16} style={{ animation: loading ? "spin 1s linear infinite" : "none" }} />
                    </button>
                    <FilterPopover
                        active={filterActive}
                        title="Фільтри заявок"
                        hint="За замовчуванням показано відкриті заявки."
                        buttonTitle="Фільтри заявок"
                        onClear={() => {
                            setStatusFilter("open");
                            setScopeFilter("network:all");
                        }}
                    >
                        <label className="toolbar-filter-field">
                            <span>Статус</span>
                            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                                <option value="open">Відкриті ({report.open})</option>
                                <option value="closed">Закриті ({report.closed})</option>
                            </select>
                        </label>
                        <label className="toolbar-filter-field">
                            <span>Область</span>
                            <select value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)}>
                                {scopeOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </label>
                    </FilterPopover>
                </div>
            </div>

            <RestrictionNotice restriction={myRestriction} module="requests" />

            {showCreateModal ? (
                <div className="modal-backdrop" onClick={() => !loading && setShowCreateModal(false)}>
                    <div className="modal-content operator-mobile-modal request-create-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Нова заявка</h3>
                        <p className="request-create-hint">
                            Можна створити загальну заявку по мережі або привʼязати її до конкретної локації та додати фото.
                        </p>

                        {connectedNetworks.length > 1 ? (
                            <>
                                <label className="client-form-label">Мережа</label>
                                <select
                                    value={createNetworkId}
                                    onChange={(e) => setCreateNetworkId(e.target.value)}
                                    className="client-input"
                                >
                                    <option value="">Оберіть мережу</option>
                                    {connectedNetworks.map((network) => (
                                        <option key={network.id} value={network.id}>{network.name}</option>
                                    ))}
                                </select>
                            </>
                        ) : null}

                        <label className="client-form-label">Локація (опційно)</label>
                        <select
                            value={createLocationId}
                            onChange={(e) => setCreateLocationId(e.target.value)}
                            className="client-input"
                            disabled={!locationsForCreate.length && !(createNetworkId || connectedNetworks.length === 1)}
                        >
                            <option value="">Без привʼязки до локації (загальна)</option>
                            {locationsForCreate.map((loc) => (
                                <option key={loc.id} value={loc.id}>{loc.label}</option>
                            ))}
                        </select>

                        <label className="client-form-label">Опис / завдання</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            placeholder="Що потрібно зробити або яка проблема..."
                            className="client-input client-input--textarea"
                        />

                        <div className="request-create-photos">
                            <div className="request-create-photos__head">
                                <span className="client-form-label" style={{ margin: 0 }}>Фото (до 5)</span>
                                <button
                                    type="button"
                                    className="add-btn add-btn--secondary request-photo-add-btn"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={loading || createFiles.length >= 5}
                                >
                                    <ImagePlus size={16} /> Додати фото
                                </button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    hidden
                                    onChange={onPickFiles}
                                />
                            </div>
                            {createFiles.length ? (
                                <div className="request-create-files">
                                    {createFiles.map((file, idx) => (
                                        <div key={`${file.name}-${idx}`} className="request-create-file-chip">
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                className="request-create-file-remove"
                                                onClick={() => setCreateFiles((prev) => prev.filter((_, i) => i !== idx))}
                                                aria-label="Прибрати фото"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="request-create-photos__empty">Фото не обовʼязкові</div>
                            )}
                        </div>

                        {message ? <p className="client-inline-message">{message}</p> : null}
                        <div className="modal-actions">
                            <button className="add-btn" onClick={submit} disabled={loading}>
                                Надіслати заявку
                            </button>
                            <button
                                type="button"
                                className="add-btn add-btn--secondary"
                                onClick={() => setShowCreateModal(false)}
                                disabled={loading}
                            >
                                Скасувати
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}

            {message && !showCreateModal ? <p className="client-inline-message">{message}</p> : null}
            {loading ? <p>⏳ Завантаження...</p> : null}

            <div className="list-card">
                {items.length === 0 ? (
                    <p>Немає заявок</p>
                ) : (
                    <div className="operator-accordion-list">
                        {items.map((item) => {
                            const isExpanded = expandedRequestId === item.id;
                            const isOwn = item.is_own !== false && (
                                selectedScopeMeta.scope === "mine" || item.is_own === true
                            );
                            const messages = Array.isArray(item.messages) ? item.messages : [];
                            const attachments = Array.isArray(item.attachments) ? item.attachments : [];
                            return (
                                <div
                                    key={item.id}
                                    ref={(el) => {
                                        if (el) requestRefs.current.set(item.id, el);
                                        else requestRefs.current.delete(item.id);
                                    }}
                                    className={`operator-accordion-item ${highlightedRequestId === item.id ? "is-highlighted" : ""}`}
                                >
                                    <button
                                        type="button"
                                        className="operator-accordion-head"
                                        onClick={() => setExpandedRequestId((prev) => (prev === item.id ? null : item.id))}
                                    >
                                        <div className="operator-accordion-head-main">
                                            <div className="operator-accordion-title">
                                                #{item.id} {item.status === "open" ? "Відкрита" : "Закрита"}
                                                {item.closed_by === "client" ? " · вами" : ""}
                                                {item.closed_by === "operator" ? " · оператором" : ""}
                                            </div>
                                            <div className="operator-accordion-meta">
                                                {selectedScopeMeta.scope === "network" ? (
                                                    <span>{item.is_own ? "Моя заявка" : "Заявка мешканця мережі"}</span>
                                                ) : null}
                                                {item.network_name ? <span>{item.network_name}</span> : null}
                                                {item.location_name ? <span>{item.location_name}</span> : <span>Загальна</span>}
                                                {attachments.length ? <span>📷 {attachments.length}</span> : null}
                                                {item.created_at ? <span>{new Date(item.created_at).toLocaleString()}</span> : null}
                                            </div>
                                            <div className="operator-accordion-preview">{getPreview(item.description)}</div>
                                        </div>
                                        <div className="operator-accordion-caret">{isExpanded ? "▾" : "▸"}</div>
                                    </button>

                                    {isExpanded ? (
                                        <div className="operator-accordion-body">
                                            <div className="operator-detail-grid">
                                                <div className="operator-detail-block operator-detail-block--full">
                                                    <span className="operator-detail-label">Опис заявки</span>
                                                    <div className="operator-detail-value">{item.description || "Без опису"}</div>
                                                </div>
                                                {item.network_name ? (
                                                    <div className="operator-detail-block">
                                                        <span className="operator-detail-label">Мережа</span>
                                                        <div className="operator-detail-value">{item.network_name}</div>
                                                    </div>
                                                ) : null}
                                                <div className="operator-detail-block">
                                                    <span className="operator-detail-label">Локація</span>
                                                    <div className="operator-detail-value">
                                                        {item.location_name || "Без привʼязки (загальна)"}
                                                    </div>
                                                </div>
                                                {attachments.length ? (
                                                    <div className="operator-detail-block operator-detail-block--full">
                                                        <span className="operator-detail-label">Фото до заявки</span>
                                                        <PhotoStrip attachments={attachments} />
                                                    </div>
                                                ) : null}
                                                {item.operator_comment ? (
                                                    <div className="operator-detail-block operator-detail-block--full">
                                                        <span className="operator-detail-label">Підсумок оператора</span>
                                                        <div className="operator-detail-note">{item.operator_comment}</div>
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="request-thread">
                                                <div className="request-thread__title">Переписка / відповіді</div>
                                                {messages.length === 0 ? (
                                                    <div className="request-thread__empty">Поки немає коментарів</div>
                                                ) : (
                                                    messages.map((msg) => (
                                                        <div
                                                            key={msg.id}
                                                            className={`request-thread__bubble is-${msg.sender_type}`}
                                                        >
                                                            <div className="request-thread__meta">
                                                                {msg.sender_type === "client" ? "Ви / клієнт" : "Оператор"}
                                                                {msg.created_at
                                                                    ? ` · ${new Date(msg.created_at).toLocaleString()}`
                                                                    : ""}
                                                            </div>
                                                            <div className="request-thread__body">{msg.body}</div>
                                                            <PhotoStrip attachments={msg.attachments} />
                                                        </div>
                                                    ))
                                                )}
                                            </div>

                                            {isOwn ? (
                                                <div className="request-own-actions">
                                                    <label className="client-form-label">Додати коментар</label>
                                                    <textarea
                                                        className="client-input client-input--textarea"
                                                        rows={3}
                                                        value={draftComments[item.id] || ""}
                                                        onChange={(e) => setDraftComments((prev) => ({
                                                            ...prev,
                                                            [item.id]: e.target.value,
                                                        }))}
                                                        placeholder="Уточнення, додаткові деталі..."
                                                    />
                                                    <div className="request-own-actions__row">
                                                        <button
                                                            type="button"
                                                            className="add-btn"
                                                            disabled={loading}
                                                            onClick={() => sendComment(item.id)}
                                                        >
                                                            Надіслати коментар
                                                        </button>
                                                    </div>

                                                    {item.status === "open" ? (
                                                        <div className="request-close-box">
                                                            <label className="client-form-label">
                                                                Закрити заявку (якщо вже не актуально)
                                                            </label>
                                                            <textarea
                                                                className="client-input client-input--textarea"
                                                                rows={2}
                                                                value={closeComments[item.id] || ""}
                                                                onChange={(e) => setCloseComments((prev) => ({
                                                                    ...prev,
                                                                    [item.id]: e.target.value,
                                                                }))}
                                                                placeholder="Коментар при закритті (опційно)"
                                                            />
                                                            <button
                                                                type="button"
                                                                className="add-btn add-btn--secondary"
                                                                disabled={loading}
                                                                onClick={() => doClose(item.id)}
                                                            >
                                                                Закрити з коментарем
                                                            </button>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientRequests;
